import { useState } from 'react';
import { Alert, Empty, Flex } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { CustomRangePicker } from '@/components/custom-range-picker/custom-range-picker';
import FiltersDisclosure from '@/components/filters-disclosure/filters-disclosure';
import Dropdown from '@/components/form/dropdown/dropdown';
import LoadMoreRows from '@/components/load-more-rows/load-more-rows';
import SearchField from '@/components/search-field/search-field';
import VsTable from '@/components/vs-table/vs-table';
import { useDebouncedFilterQuery } from '@/hooks/use-debounced-filter-query/use-debounced-filter-query';
import { useOutboundCalls } from '@/hooks/use-outbound-calls/use-outbound-calls';
import { useOutboundCampaigns } from '@/hooks/use-outbound-campaigns/use-outbound-campaigns';
import { useOutboundScenarios } from '@/hooks/use-outbound-scenarios/use-outbound-scenarios';
import { OutboundCall } from '@/services/outbound-calls-service/calls.service.types';
import {
  DISPOSITIONS,
  OUTBOUND_MAX_ROWS,
  OUTBOUND_PAGE_SIZE,
  TRIGGER_SOURCES,
} from '@/services/outbound-calls-service/outbound-calls.constants';
import { calculateSecondsToMinutes } from '@/utils/helpers';
import { LeadDrawer } from '../campaigns/lead-drawer';
import { CallOutcome, TransferOutcome } from '../call-result';
import { useOutboundContext } from '../outbound-calls.context';
import { outboundErrorMessage } from '../outbound-calls.messages';
import { dispositionLabel, formatDateTime, triggerSourceLabel } from '../outbound-calls.utils';
import { CallDrawer } from './call-drawer';
import styles from '../outbound-calls.module.scss';

type CallFilters = {
  query?: string;
  scenario_id?: number;
  campaign_id?: number;
  disposition?: string;
  trigger_source?: string;
  objective?: 'true' | 'false';
  start_time?: string;
  end_time?: string;
};

/** Every outbound call of the company: campaign attempts and test calls alike. */
export function OutboundCallsHistory() {
  const { t } = useTranslation(['outbound', 'common']);
  const { company } = useOutboundContext();
  const { scenarios } = useOutboundScenarios(company.id);
  const { campaigns } = useOutboundCampaigns(company.id);
  const [filters, setFilters] = useState<CallFilters>({});
  const [dates, setDates] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  // Pages loaded, as on the conversations list: "Pokaż więcej" asks for that many pages at once.
  const [loadedPages, setLoadedPages] = useState(1);
  const [callId, setCallId] = useState<number | undefined>();
  const [leadId, setLeadId] = useState<number | undefined>();

  const changeFilters = (next: CallFilters) => {
    setFilters({ ...next, query: next.query?.trim() || undefined });
    setLoadedPages(1);
  };
  const { filterValue, onQueryChange } = useDebouncedFilterQuery(filters, changeFilters);
  const set = (patch: Partial<CallFilters>) => changeFilters({ ...filters, ...patch });

  const rows = Math.min(OUTBOUND_PAGE_SIZE * loadedPages, OUTBOUND_MAX_ROWS);
  const { objective, ...params } = filters;
  const { calls, total, isLoading, isFetching, error } = useOutboundCalls({
    ...params,
    company_id: company.id,
    objective_completed: objective === undefined ? undefined : objective === 'true',
    page: 1,
    items_per_page: rows,
  });

  // What the panel alone sets, so a collapsed panel cannot hide a working filter.
  const activeCount =
    (filters.start_time ? 1 : 0) +
    (filters.campaign_id ? 1 : 0) +
    (filters.trigger_source ? 1 : 0) +
    (filters.objective ? 1 : 0);

  const columns: ColumnsType<OutboundCall> = [
    {
      title: t('outbound:calls.columns.date'),
      key: 'created_at',
      render: (_, call) => <span className={styles.nowrap}>{formatDateTime(call.created_at)}</span>,
    },
    {
      title: t('outbound:calls.columns.callee'),
      key: 'callee',
      render: (_, call) => (
        <span className={styles.stack}>
          <b>{call.lead?.name ?? call.phone ?? t('outbound:none')}</b>
          <span className={styles.secondary}>
            {[call.contact?.full_name, call.contact?.position, call.lead ? call.phone : null]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </span>
      ),
    },
    {
      title: t('outbound:calls.columns.scenario'),
      key: 'scenario',
      render: (_, call) => (
        <span className={styles.stack}>
          <span>{call.scenario_name ?? t('outbound:none')}</span>
          {call.campaign && <span className={styles.secondary}>{call.campaign.name}</span>}
        </span>
      ),
    },
    {
      title: t('outbound:calls.columns.source'),
      key: 'source',
      render: (_, call) => (
        <span className={styles.stack}>
          <span>{triggerSourceLabel(call.trigger_source)}</span>
          {call.triggered_by && <span className={styles.secondary}>{call.triggered_by}</span>}
        </span>
      ),
    },
    {
      title: t('outbound:calls.columns.outcome'),
      key: 'outcome',
      render: (_, call) => (
        <CallOutcome
          disposition={call.disposition}
          objectiveCompleted={call.objective_completed}
          live={call.status === 'live'}
        />
      ),
    },
    {
      title: t('outbound:calls.columns.transfer'),
      key: 'transfer',
      render: (_, call) => <TransferOutcome call={call} />,
    },
    {
      title: t('outbound:calls.columns.duration'),
      key: 'duration',
      render: (_, call) =>
        call.duration ? calculateSecondsToMinutes(call.duration) : t('outbound:none'),
    },
  ];

  return (
    <Flex vertical gap={14}>
      <div className={styles.toolbar}>
        <div className={styles.actions}>
          <SearchField
            value={filterValue}
            onChange={onQueryChange}
            placeholder={t('outbound:calls.searchPlaceholder')}
          />
          <Dropdown
            aria-label={t('outbound:calls.allScenarios')}
            wrapperClassName={styles.control}
            placeholder={t('outbound:calls.allScenarios')}
            value={filters.scenario_id}
            onChange={(value: number | undefined) => set({ scenario_id: value })}
            options={scenarios.map((scenario) => ({ value: scenario.id, label: scenario.name }))}
          />
          <Dropdown
            aria-label={t('outbound:calls.allDispositions')}
            wrapperClassName={styles.control}
            placeholder={t('outbound:calls.allDispositions')}
            value={filters.disposition}
            onChange={(value: string | undefined) => set({ disposition: value })}
            options={DISPOSITIONS.map((value) => ({ value, label: dispositionLabel(value) }))}
          />
          <FiltersDisclosure activeCount={activeCount}>
            <CustomRangePicker
              dates={dates}
              setDates={setDates}
              format="YYYY-MM-DD"
              onClear={() => set({ start_time: undefined, end_time: undefined })}
              onChange={({ time_range }) =>
                set({
                  start_time: time_range.start_time || undefined,
                  end_time: time_range.end_time || undefined,
                })
              }
            />
            <Dropdown
              aria-label={t('outbound:calls.allCampaigns')}
              wrapperClassName={styles.control}
              placeholder={t('outbound:calls.allCampaigns')}
              value={filters.campaign_id}
              onChange={(value: number | undefined) => set({ campaign_id: value })}
              options={campaigns.map((campaign) => ({ value: campaign.id, label: campaign.name }))}
            />
            <Dropdown
              aria-label={t('outbound:calls.allSources')}
              wrapperClassName={styles.control}
              placeholder={t('outbound:calls.allSources')}
              value={filters.trigger_source}
              onChange={(value: string | undefined) => set({ trigger_source: value })}
              options={TRIGGER_SOURCES.map((value) => ({
                value,
                label: triggerSourceLabel(value),
              }))}
            />
            <Dropdown
              aria-label={t('outbound:calls.anyObjective')}
              wrapperClassName={styles.control}
              placeholder={t('outbound:calls.anyObjective')}
              value={filters.objective}
              onChange={(value: CallFilters['objective']) => set({ objective: value })}
              options={[
                { value: 'true', label: t('outbound:objective.completed') },
                { value: 'false', label: t('outbound:objective.notCompleted') },
              ]}
            />
          </FiltersDisclosure>
        </div>
      </div>

      {error ? (
        <Alert
          type="error"
          showIcon
          message={outboundErrorMessage(error, t('outbound:errors.load'))}
        />
      ) : (
        <>
          <VsTable<OutboundCall>
            rowKey="id"
            loading={isLoading}
            dataSource={calls}
            columns={columns}
            scroll={{ x: 1000 }}
            onRow={(call) => ({
              onClick: () => setCallId(call.id),
              className: styles.clickable,
            })}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={t('outbound:calls.empty')}
                />
              ),
            }}
          />
          {calls.length < total && (
            <LoadMoreRows
              remaining={total - calls.length}
              atCeiling={rows >= OUTBOUND_MAX_ROWS}
              ceiling={OUTBOUND_MAX_ROWS}
              isLoading={isFetching}
              onClick={() => setLoadedPages((pages) => pages + 1)}
            />
          )}
        </>
      )}

      <CallDrawer
        callId={callId}
        onClose={() => setCallId(undefined)}
        onOpenLead={(id) => {
          setCallId(undefined);
          setLeadId(id);
        }}
      />
      <LeadDrawer leadId={leadId} onClose={() => setLeadId(undefined)} />
    </Flex>
  );
}
